<template>
  <UDropdownMenu
    v-if="current"
    :items="menuItems"
    :content="{ align: 'end', side: 'bottom' }"
  >
    <button
      type="button"
      aria-haspopup="menu"
      class="flex h-[46px] w-[178px] shrink-0 items-center justify-between gap-2 overflow-hidden rounded-lg border border-[var(--ui-border)] px-2 text-start transition hover:bg-[var(--ui-bg-elevated)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
      :disabled="switching"
    >
      <UUser
        :name="current.brandName"
        :description="current.subdomain"
        :avatar="avatarProps"
        size="sm"
        :ui="{ root: 'min-w-0 flex-1', wrapper: 'min-w-0', name: 'truncate', description: 'truncate' }"
      />
      <UIcon
        name="i-lucide-chevron-down"
        class="size-4 shrink-0 text-[var(--ui-text-muted)]"
      />
    </button>
  </UDropdownMenu>
</template>

<script setup lang="ts">
import { NEW_SITE_KEY, useOnboardingStore } from '~/stores/useOnboardingStore'
import { useAuthStore } from '~/stores/useAuthStore'
import { apiErrorMessage } from '~/utils/apiError'

const auth = useAuthStore()
const route = useRoute()
const toast = useToast()
const switching = useState('site-switch-loading', () => false)

const sites = computed(() => auth.sites)
const current = computed(() => sites.value.find((site) => site.id === auth.site?.id) ?? sites.value[0] ?? null)
const isOwner = computed(() => sites.value.some((site) => site.role === 'owner'))

const avatarProps = computed(() => ({
  alt: current.value?.brandName ?? 'سایت',
  icon: 'i-lucide-globe',
}))

const menuItems = computed(() => {
  const groups = [
    sites.value.map((site) => ({
      label: site.brandName,
      icon: site.id === current.value?.id ? 'i-lucide-check' : 'i-lucide-globe',
      disabled: site.id === current.value?.id || switching.value,
      onSelect: () => switchTo(site.id),
    })),
  ]
  if (isOwner.value) {
    groups.push([
      {
        label: 'افزودن سایت جدید',
        icon: 'i-lucide-plus',
        onSelect: () => addSite(),
      },
    ])
  }
  return groups
})

async function switchTo(siteId: string) {
  if (switching.value || siteId === current.value?.id) return
  const started = Date.now()
  switching.value = true
  try {
    await $fetch('/api/me/sites/active', { method: 'POST', body: { siteId } })
    clearNuxtData()
    await auth.hydrate()
    await navigateTo('/dashboard')
    if (route.path === '/dashboard') await refreshNuxtData()
    const wait = Math.max(0, 280 - (Date.now() - started))
    if (wait) await new Promise((resolve) => setTimeout(resolve, wait))
  }
  catch (error) {
    toast.add({ title: 'سوئیچ سایت ناموفق بود', description: apiErrorMessage(error), color: 'error' })
  }
  finally {
    switching.value = false
  }
}

async function addSite() {
  const onboarding = useOnboardingStore()
  onboarding.reset()
  sessionStorage.setItem(NEW_SITE_KEY, '1')
  onboarding.patchAnswers({
    firstName: auth.user?.firstName ?? '',
    lastName: auth.user?.lastName ?? '',
    nationalId: auth.user?.nationalId ?? '',
    phone: auth.user?.phone ?? '',
  })
  await navigateTo('/onboarding/step-1')
}
</script>
